import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpEvent} from "@angular/common/http";
import {map, Observable} from "rxjs";
import {baseUrl} from "../../../../core/constants/base-url";
import {telegramResourceType} from "../configs/telegram-page-config";
import {
  AccountQueryParams,
  ChatsQueryParams,
  CreateChatQueryParams,
  GetFileParams,
  MessagesQueryParams,
  SendDocumentQueryParams,
  SendImageQueryParams,
  SendMessageQueryParams,
  SendVideoQueryParams,
  TelegramAccount,
  UpdateAccountPhotoParams,
  UserChatQueryParams
} from "../types/telegram-page.types";
import {ChatTelegram} from "../types/chat-telegram.types";
import {UserTelegram} from "../types/user-telegram.types";
import {MessageTelegram} from "../types/last-message.types";
import {SaveFileParams} from "../types/telegram-save-file-modal.types";
import {AttachChatRecordCalenderIdParams} from "../types/attach-chat-record-calender";
import {downloadImageHeaders, downloadVideoHeaders} from "../../../../core/models/http-headers";

@Injectable({
  providedIn: 'root'
})
export class TelegramApiService {
  private http: HttpClient = inject(HttpClient);
  private url: string = `${baseUrl}/${telegramResourceType.path}`;

  public getMainChats(chatsQueryParams: ChatsQueryParams): Observable<Array<ChatTelegram>> {
    return this.http.get<Array<ChatTelegram>>(`${this.url}/main`, { params: chatsQueryParams });
  }

  public getAccounts(): Observable<Array<TelegramAccount>> {
    return this.http.get<Array<TelegramAccount>>(`${this.url}/accounts`);
  }

  public getUserChat(userChatQueryParams: UserChatQueryParams): Observable<number> {
    return this.http.get<number>(`${this.url}/user-chat`, { params: userChatQueryParams });
  }

  public getArchiveChat(chatsQueryParams: ChatsQueryParams): Observable<Array<ChatTelegram>> {
    return this.http.get<Array<ChatTelegram>>(`${this.url}/archive`, { params: chatsQueryParams });
  }

  public getAccount(accountQueryParams: AccountQueryParams): Observable<UserTelegram> {
    return this.http.get<UserTelegram>(`${this.url}/account`, { params: accountQueryParams });
  }

  public getMessages(getMessagesQueryParams: MessagesQueryParams): Observable<Array<MessageTelegram>> {
    const { chatId, limit, accountId } = getMessagesQueryParams;
    const params = {
      limit: limit,
      accountId: accountId
    }

    return this.http.get<Array<MessageTelegram>>(`${this.url}/${chatId}/messages`, { params: params });
  }

  public sendMessage(sendMessageQueryParams: SendMessageQueryParams): Observable<void> {
      return this.http.post<void>(`${this.url}/text`,{}, { params: sendMessageQueryParams });
  }

  public sendImageMessage(sendImageQueryParams: SendImageQueryParams): Observable<void> {
    const { chatId, message, accountId, image } = sendImageQueryParams;
    const params = {
      chatId: chatId,
      message: message,
      accountId: accountId
    };

    return this.http.post<void>(`${this.url}/image`, image, { params: params });
  }

  public createChat(createChatQueryParams: CreateChatQueryParams): Observable<number> {
    const { userId, accountId } = createChatQueryParams;
    const params = {
      accountId: accountId
    };

    return this.http.post<number>(`${this.url}/create-chat/${userId}`, params);
  }

  public getImage(getFileParams: GetFileParams): Observable<string> {
    const { accountId, remoteId } = getFileParams;

    return this.http.get(`${this.url}/file/${remoteId}`, {
      params: {
        accountId: accountId,
      },
      headers: downloadImageHeaders,
      responseType: 'blob'
    }).pipe(
      map(blob => {
        return URL.createObjectURL(blob);
      })
    );
  }

  public sendVideoMessage(sendVideoQueryParams: SendVideoQueryParams): Observable<HttpEvent<void>> {
    const { chatId, message, accountId, video } = sendVideoQueryParams;
    const params = {
      chatId: chatId,
      message: message,
      accountId: accountId
    };

    return this.http.post<void>(`${this.url}/videos`, video, {
      params: params,
      reportProgress: true,
      observe: 'events'
    });
  }

  public getVideo(getFileParams: GetFileParams): Observable<HttpEvent<Blob>> {
    const { accountId, remoteId } = getFileParams;

    return this.http.get(`${this.url}/video/${remoteId}`, {
      params: {
        accountId: accountId,
      },
      headers: downloadVideoHeaders,
      responseType: 'blob',
      reportProgress: true,
      observe: 'events'
    });
  }

  public updateAccountPhoto({ accountId, image }: UpdateAccountPhotoParams): Observable<HttpEvent<void>> {
    return this.http.post<void>(`${this.url}/profile/images`, image, {
      params: {
        accountId: accountId
      },
      reportProgress: true,
      observe: 'events'
    });
  };

  public sendDocumentMessage(sendDocumentQueryParams: SendDocumentQueryParams): Observable<HttpEvent<void>> {
    const { chatId, message, accountId, document } = sendDocumentQueryParams;
    const params = {
      chatId: chatId,
      message: message,
      accountId: accountId
    };

    return this.http.post<void>(`${this.url}/documents`, document, {
      params: params,
      reportProgress: true,
      observe: 'events'
    });
  };

  public getDocument(getFileParams: GetFileParams): Observable<HttpEvent<Blob>> {
    const { accountId, remoteId } = getFileParams;

    return this.http.get(`${this.url}/document/${remoteId}`, {
      params: {
        accountId: accountId,
      },
      headers: downloadVideoHeaders,
      responseType: 'blob',
      reportProgress: true,
      observe: 'events'
    });
  }

  public saveFile(saveFileParams: SaveFileParams): Observable<void> {
    return this.http.post<void>(`${this.url}/file/upload`, {}, {
      params: saveFileParams
    });
  }

  public attachChatRecordCalenderId(attachChatRecordCalenderIdParams: AttachChatRecordCalenderIdParams): Observable<void> {
    return this.http.post<void>(`${this.url}/record-calendar/attach`, {}, {
      params: attachChatRecordCalenderIdParams
    });
  }
}
